import { IDefaultModel, Model } from "@/types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search } from "lucide-react";
const ModelMenu = ({
  models,
  selectedModel,
  onModelChange,
  type,
  defaultModels,
}: {
  models?: Array<Model>;
  selectedModel?: string;
  onModelChange: (modelId: string) => void;
  type: string;
  defaultModels?: IDefaultModel[];
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const defaultModelId = defaultModels?.find((m) =>
    type === "base" ? m.mode === "base" : m.mode === "planner",
  )?.llm_config.id;

  const filteredModels = useMemo(() => {
    if (!models) return [];
    return models.filter((model) => {
      const isSupportedType = model.is_chat_model || model.is_multimodal;

      const isLegacyType =
        !model.is_chat_model &&
        !model.is_multimodal &&
        !model.is_default &&
        !model.is_embedding_model &&
        !model.is_image_generation_model &&
        !model.is_shared &&
        !model.is_stt_model &&
        !model.is_tts_model;

      return isSupportedType || isLegacyType;
    });
  }, [models]);

  const searchedModels = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase();
    if (!query) return filteredModels;
    return filteredModels.filter((model) =>
      model.name.toLocaleLowerCase().includes(query),
    );
  }, [filteredModels, searchQuery]);

  const selectedModelName = filteredModels.find(
    (model) => model.id === selectedModel,
  )?.name;

  return (
    <Select value={selectedModel} onValueChange={onModelChange}>
      <SelectTrigger className="w-full bg-input text-foreground placeholder:text-muted-foreground h-11 rounded-[8px] px-4 py-3 border border-border">
        <SelectValue placeholder={t("playground.selectModel")}>
          {selectedModelName}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <div
          className="relative mb-1 px-1"
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t("playground.searchPlaceholder")}
            aria-label={t("playground.searchPlaceholder")}
            className="h-9 w-full rounded-md border border-border bg-transparent ps-9 pe-3 text-sm text-foreground outline-hidden placeholder:text-muted-foreground focus:border-primary"
          />
        </div>
        <div className="max-h-[320px] overflow-y-auto overscroll-contain pe-1 [scrollbar-color:var(--primary)_transparent]">
          {searchedModels.map((model) => (
            <SelectItem
              key={type + model.id}
              value={model.id}
              className="flex min-w-0 items-center justify-between gap-2 rounded-[35px] border border-transparent outline-hidden data-[highlighted]:border-foreground data-[highlighted]:bg-accent focus:border-foreground focus:bg-accent focus:outline-hidden focus-visible:outline-hidden focus-visible:ring-0"
            >
              <span className="min-w-0 flex-1 truncate">{model.name}</span>
              {model.id === defaultModelId && (
                <span className="bg-primary text-primary-foreground text-[10px] mx-2 px-2 py-0.5 rounded-full font-medium">
                  {t("playground.default")}
                </span>
              )}
            </SelectItem>
          ))}
          {searchedModels.length === 0 && (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              {t("usageAndCost.llmConfigs.noModelsFound")}
            </p>
          )}
        </div>
      </SelectContent>
    </Select>
  );
};

export default ModelMenu;
